<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import Store from '@store/dataProvider';
import { IPagination } from '../../types/pagination.type';
import { useToast } from '../../composables/useToast';
import TypeAssetsRepository from '@repositories/typeAssets.repository';
import { ITypeAsset } from '../../types/typeAssets.type';
import CompaniesRepository from '@repositories/companies.repository';
import CountriesRepository from '@repositories/countries.repository';
import ReinsurersRepository from '@repositories/reinsurers.repository';
import { ICompany } from '../../types/company.type';
import { ICountry } from '../../types/countries.type';
import { IReinsurer } from '../../types/reinsurer.type';
import { useI18n } from "vue-i18n";
import DataTable from "@components/DataTable.vue";

const isLoadingTypeAssets = ref<boolean>(false);

const { t } = useI18n();
const store = Store();
const { $toast } = useToast();
const typeAssets = ref<ITypeAsset[]>([]);
const companies = ref<ICompany[]>([]);
const countries = ref<ICountry[]>([]);
const reinsurers = ref<IReinsurer[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const selectedTypeAsset = ref<ITypeAsset | null>(null);
const isViewActive = ref<boolean>(true);
const searchFormRef = ref<any>(null);

const fieldsTableInactive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'name',
    label: t('global.nombre'),
  },
  {
    key: 'restore',
    label: t('global.restaurar'),
  },
]);

const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'name',
    label: t('global.nombre'),
  },
  {
    key: 'company',
    label: t('global.compania'),
    formatter: (e: any) => e?.name || '-',
  },
  {
    key: 'country',
    label: t('global.pais'),
    formatter: (e: any) => e?.description || '-',
  },
  {
    key: 'reinsurer',
    label: t('global.reasegurador'),
    formatter: (e: any) => e?.name || '-',
  },
  {
    key: 'typeAssetInfo',
    label: t('global.editar'),
  },
  {
    key: 'delete',
    label: t('global.eliminar'),
  },
]);

const fieldsFilter: any = ref([
  {
    key: 'search',
    title: t('global.buscar'),
    type: 'text',
    placeholder: t('ajustes.typeAssets.buscar_por_nombre'),
    value: null,
    cols: 3,
  },
]);

const getTypeAssetsFromAPI = async (
  page: number,
  deleted: boolean = false,
  params = {},
) => {
  isLoadingTypeAssets.value = true;
  try {
    const data = await TypeAssetsRepository.getTypeAssetsProPaginated({
      page,
      deleted,
      ...params,
    });
    const typeAssetsData = data?.typeAssets || data?.data || [];
    if (typeAssetsData && Array.isArray(typeAssetsData)) {
      paginationData.value = data?.pagination;
      updateTypeAssets(typeAssetsData);
      return typeAssetsData;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching typeAssets:', error);
    return [];
  } finally {
    isLoadingTypeAssets.value = false;
  }
};

const getCompaniesFromApi = async () => {
  try {
    const data = await CompaniesRepository.getCompanies(true);
    if (Array.isArray(data?.companies)) {
      companies.value = data.companies;
    }
  } catch (error) {
    console.error('Error while fetching companies:', error);
  }
};

const getCountriesFromApi = async () => {
  try {
    const data = await CountriesRepository.getcountries({page: 1, blocked: false});
    if (Array.isArray(data?.data?.countries)) {
      countries.value = data.data.countries;
    }
  } catch (error) {
    console.error('Error while fetching countries:', error);
  }
};

const getReinsurersFromApi = async () => {
  try {
    const data = await ReinsurersRepository.getReinsurers();
    if (Array.isArray(data?.reinsurers)) {
      reinsurers.value = data.reinsurers;
    }
  } catch (error) {
    console.error('Error while fetching reinsurers:', error);
  }
};

const updateTypeAssets = (newTypeAssets: ITypeAsset[]) => {
  typeAssets.value = newTypeAssets;
};

const loadTypeAssets = async () => {
  await getTypeAssetsFromAPI(1);
};

onMounted(() => {
  loadTypeAssets();
  getCompaniesFromApi();
  getCountriesFromApi();
  getReinsurersFromApi();
});

const fieldsInputTypeAssets = computed(() => {
  return [
    {
      key: 'name',
      title: t('global.nombre'),
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: t('global.nombre'),
      value: selectedTypeAsset.value?.name,
      cols: 12,
    },
    {
      key: 'companyId',
      title: t('global.compania'),
      type: 'select',
      label: 'name',
      validations: {
        rules: {
          required: false,
        },
      },
      options: companies,
      placeholder: t('global.compania'),
      value: selectedTypeAsset.value?.companyId,
      formatValue: (val: any) => val?.id,
      cols: 12,
    },
    {
      key: 'paisId',
      title: t('global.pais'),
      type: 'select',
      label: 'description',
      validations: {
        rules: {
          required: false,
        },
      },
      options: countries,
      placeholder: t('global.pais'),
      value: selectedTypeAsset.value?.paisId,
      formatValue: (val: any) => val?.id,
      cols: 12,
    },
    {
      key: 'reinsurerId',
      title: t('global.reasegurador'),
      type: 'select',
      label: 'name',
      options: reinsurers,
      placeholder: t('global.reasegurador'),
      value: selectedTypeAsset.value?.reinsurerId,
      formatValue: (val: any) => val?.id,
      cols: 12,
    },
  ];
});

const onChangePage = async (page: number) => {
  getTypeAssetsFromAPI(page);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = t('global.agregar') + ' Tipo de Activo';
  isModalOpen.value = true;
  modalType.value = 'modalCreateTypeAsset';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedTypeAsset.value = null;
};

const handleActive = async () => {
  const typeAssetsData = await getTypeAssetsFromAPI(1);
  updateTypeAssets(typeAssetsData);
  isViewActive.value = true;
};

const handleInactive = async () => {
  const typeAssetsDeleted = await getTypeAssetsFromAPI(1, true);
  updateTypeAssets(typeAssetsDeleted);
  isViewActive.value = false;
};

const handleOpenModalDialog = (item: ITypeAsset) => {
  modalTitle.value = isViewActive.value
    ? `${t('ajustes.typeAssets.eliminar_tipo_asset')} ID: ${item.id}`
    : `${t('ajustes.typeAssets.restaurar_tipo_asset')} ${item.name}`;
  modalType.value = 'modalDialog';
  selectedTypeAsset.value = item;
  isModalOpen.value = true;
};

const handleOnSubmit = async ({ items }: any) => {
  getTypeAssetsFromAPI(1, false, items);
};

const handleReset = async () => {
  if (!searchFormRef.value) return;
  await searchFormRef.value.resetValues();
  getTypeAssetsFromAPI(1, false);
};

const handleCreateTypeAsset = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;
  try {
    const dataToSend = {
      ...items,
    };
    await TypeAssetsRepository.createTypeAsset(dataToSend);
    $toast?.success(t('global.creado_con_exito'));
  } catch (error) {
    $toast?.error(t('global.error_creacion'));
  }
  getTypeAssetsFromAPI(paginationData.value?.current_page ?? 1, !isViewActive.value);
  handleClose();
};

const handleSelectTypeAsset = (typeAssetData: ITypeAsset) => {
  modalTitle.value = t('global.actualizar') + ' Tipo de Asset';
  (isModalOpen.value = true), (modalType.value = 'modalUpdateTypeAsset');
  selectedTypeAsset.value = typeAssetData;
};

const handleUpdateTypeAsset = async ({ items, isFormValid }: any) => {
  if (!isFormValid || !selectedTypeAsset.value) return;
  const typeAssetForUpdate = {
    ...items,
    id: selectedTypeAsset.value.id,
  };

  try {
    await TypeAssetsRepository.updateTypeAsset(typeAssetForUpdate);
    $toast?.success(t('global.actualizado'));
  } catch (error) {
    $toast?.error(t('global.error_actualizacion'));
  }
  getTypeAssetsFromAPI(paginationData.value?.current_page ?? 1, !isViewActive.value);
  handleClose();
};

const handleDeleteRestoreTypeAsset = async () => {
  if (selectedTypeAsset?.value?.id) {
    let response = null;
    try {
      if (isViewActive.value) {
        response = await TypeAssetsRepository.deleteTypeAsset(
          selectedTypeAsset.value.id,
        );
      } else {
        response = await TypeAssetsRepository.restoreTypeAsset(
          selectedTypeAsset.value.id,
        );
      }
    } catch (error) {
      if (isViewActive.value) {
        return $toast?.error(t('global.error_eliminacion'));
      }
      $toast?.error(t('global.error_restauracion'));
    }
    getTypeAssetsFromAPI(paginationData.value?.current_page ?? 1, !isViewActive.value);
    handleClose();
  }
};
</script>

<template>
  <Layout title="Tipos de Assets" :textColor="'color-secondary'">
    <template #layout>
      <div class="d-flex justify-content-between">
        <div>
          <CButton
            class="background-blue button__form"
            style="padding: 0.5rem !important; margin-right: 15px"
            type="button"
            @click="handleActive"
          >
            <template #content>{{ t('global.activos') }}</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleInactive"
          >
            <template #content>{{ t('global.inactivos') }}</template>
          </CButton>
        </div>
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleOpenModalCreate"
        >
          <template #content>{{ t('ajustes.typeAssets.agregar_tipo_asset') }}</template>
        </CButton>
      </div>

      <div class="form__logEmail">
        <Form
          ref="searchFormRef"
          @onSubmit="handleOnSubmit"
          :inputs="fieldsFilter"
          customClass="poliza__form"
        >
          <template #buttons>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
            >
              <template #content>{{ t('global.buscar') }}</template>
            </CButton>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
              type="button"
              @click="handleReset"
            >
              <template #content>{{ t('global.limpiar_filtro') }}</template>
            </CButton>
          </template>
        </Form>
      </div>

      <DataTable
        :loading="isLoadingTypeAssets"
        :fields="isViewActive ? fieldsTableActive : fieldsTableInactive"
        :items="typeAssets"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(typeAssetInfo)="assetData">
          <div
            @click="handleSelectTypeAsset(assetData.data.item)"
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
        <template v-slot:cell(delete)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon"
          >
            <img src="/icons/basura.svg" alt="eliminar" />
          </div>
        </template>
        <template v-slot:cell(restore)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon restore"
          >
            <img src="/icons/restaurar.svg" alt="restore" />
          </div>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
      >
        <template v-if="modalType === 'modalCreateTypeAsset'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputTypeAssets"
            @onSubmit="handleCreateTypeAsset"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('global.agregar') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalUpdateTypeAsset'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputTypeAssets"
            @onSubmit="handleUpdateTypeAsset"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('global.actualizar') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalDialog'">
          <h5 class="text-center">
            {{
              isViewActive
                ? `${t('ajustes.typeAssets.seguro_de_borrar')} ${selectedTypeAsset?.name}?`
                : `${t('ajustes.typeAssets.seguro_de_restaurar')} ${selectedTypeAsset?.name}?`
            }}
          </h5>
          <div class="d-flex mt-3">
            <CButton
              class="w-100 fs-6 p-2"
              background="background-pink"
              @click="handleDeleteRestoreTypeAsset"
            >
              <template #content>
                <div>
                  <span>{{ t('global.confirmar') }}</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
      </BModal>
    </template>
  </Layout>
</template>
